import React from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TableSortLabel,
  Skeleton,
} from '@mui/material'
import useDashboardListFilters from '../../../util/useDashboardListFilters'
import { useDashboardTablePatients } from '../../../queries/hooks/useDashboardTablePatients'
import { useDashboardTableSort, useResetDashboardPage } from './_hooks'
import { getErrorMessage } from '../../../util/errorHandling'
import { formatDateOnly } from './_helpers'
import type { DashboardSortableTableOptions } from '@counsel-project/counsel-audit-ai-api'
import { getSeverityCount } from '../../../util/severityCounts'
import {
  DashboardTablePagination,
  DateOfServiceCell,
  DateOfServiceHeaderCell,
} from '../dashboardListUi'

type SortableField =
  | 'name'
  | 'latestDate'
  | 'dateOfService'
  | 'documentCount'
  | 'lowAlerts'
  | 'mediumAlerts'
  | 'criticalAlerts'

type DashboardSortBy = NonNullable<DashboardSortableTableOptions['sortBy']>

const sortFieldToApiSortBy: Record<SortableField, DashboardSortBy> = {
  name: 'patientName',
  latestDate: 'mostRecentDocumentAt',
  dateOfService: 'dateOfService',
  documentCount: 'totalDocuments',
  lowAlerts: 'severity1' as DashboardSortBy,
  mediumAlerts: 'severity2' as DashboardSortBy,
  criticalAlerts: 'severity3' as DashboardSortBy,
}

const COLUMN_WIDTHS = {
  name: '26%',
  date: '13%',
  dateOfService: '15%',
  documents: '12%',
  low: '11%',
  medium: '11%',
  high: '12%',
} as const

const NAME_CELL_SX = {
  width: COLUMN_WIDTHS.name,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const

const PatientTable: React.FC = () => {
  const { filters, updateFilters } = useDashboardListFilters()
  const { orderBy, order, handleRequestSort } = useDashboardTableSort<SortableField>('name')

  const { data, error, isLoading, currentPage, setCurrentPage, rowsPerPage } =
    useDashboardTablePatients({
      sortBy: sortFieldToApiSortBy[orderBy],
      sortOrder: order,
    })
  const { results: patientRows = [], pagination } = data ?? {}
  const totalPatients = pagination?.total ?? 0

  useResetDashboardPage({ filters, setCurrentPage, resetKey: `${orderBy}:${order}` })

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage + 1)
  }

  // Handle row click
  const handleRowClick = (patientMrn: string) => {
    updateFilters({ detailViewPatientMrn: patientMrn })
  }

  const renderLoadingRows = () => {
    return Array.from({ length: rowsPerPage }, (_, rowNumber) => (
      <TableRow key={`loading-row-${rowNumber + 1}`}>
        <TableCell sx={NAME_CELL_SX}>
          <Skeleton variant="rectangular" width="80%" height={20} />
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.date }}>
          <Skeleton variant="rectangular" width="90%" height={20} />
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.dateOfService }}>
          <Skeleton variant="rectangular" width="90%" height={20} />
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.documents }}>
          <Skeleton variant="rectangular" width="60%" height={20} />
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.low }}>
          <Skeleton variant="rectangular" width="50%" height={20} />
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.medium }}>
          <Skeleton variant="rectangular" width="50%" height={20} />
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.high }}>
          <Skeleton variant="rectangular" width="50%" height={20} />
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Patients
      </Typography>
      <TableContainer component={Paper} elevation={0}>
        <Table aria-label="patient table" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: COLUMN_WIDTHS.name }}>
                <TableSortLabel
                  data-qa-id="patients-sort-name"
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.date }}>
                <TableSortLabel
                  data-qa-id="patients-sort-date"
                  active={orderBy === 'latestDate'}
                  direction={orderBy === 'latestDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('latestDate')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
                <DateOfServiceHeaderCell
                  qaId="patients-sort-date-of-service"
                  field="dateOfService"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleRequestSort}
                  width={COLUMN_WIDTHS.dateOfService}
                />
              <TableCell sx={{ width: COLUMN_WIDTHS.documents }}>
                <TableSortLabel
                  data-qa-id="patients-sort-documents"
                  active={orderBy === 'documentCount'}
                  direction={orderBy === 'documentCount' ? order : 'asc'}
                  onClick={() => handleRequestSort('documentCount')}
                >
                  Documents Scanned
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.low }}>
                <TableSortLabel
                  data-qa-id="patients-sort-low"
                  active={orderBy === 'lowAlerts'}
                  direction={orderBy === 'lowAlerts' ? order : 'asc'}
                  onClick={() => handleRequestSort('lowAlerts')}
                >
                  Low Severity Alerts
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.medium }}>
                <TableSortLabel
                  data-qa-id="patients-sort-medium"
                  active={orderBy === 'mediumAlerts'}
                  direction={orderBy === 'mediumAlerts' ? order : 'asc'}
                  onClick={() => handleRequestSort('mediumAlerts')}
                >
                  Medium Severity Alerts
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.high }}>
                <TableSortLabel
                  data-qa-id="patients-sort-high"
                  active={orderBy === 'criticalAlerts'}
                  direction={orderBy === 'criticalAlerts' ? order : 'asc'}
                  onClick={() => handleRequestSort('criticalAlerts')}
                >
                  High Severity Alerts
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && renderLoadingRows()}
            {patientRows.map((summary, index) => (
              <TableRow
                key={summary.patientMrn || `patient-row-${index}`}
                data-qa-id={`patients-table-row-${summary.patientMrn || index}`}
                hover
                onClick={() => handleRowClick(summary.patientMrn)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={NAME_CELL_SX}>
                  {summary.patientName} {summary.patientMrn && `(${summary.patientMrn})`}
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.date }}>
                  {formatDateOnly(summary.mostRecentDocumentAt)}
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.dateOfService }}>
                  <DateOfServiceCell visitDate={summary.visitDate} />
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.documents }}>
                  {summary.totalDocuments}
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.low }}>
                  {getSeverityCount(summary, '1')}
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.medium }}>
                  {getSeverityCount(summary, '2')}
                </TableCell>
                <TableCell sx={{ width: COLUMN_WIDTHS.high }}>
                  {getSeverityCount(summary, '3')}
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && patientRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {error
                    ? getErrorMessage(error, 'Please try different filters or date range')
                    : 'No patient data available'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <DashboardTablePagination
        count={totalPatients}
        rowsPerPage={rowsPerPage}
        page={Math.max(0, currentPage - 1)}
        onPageChange={handleChangePage}
        isLoading={isLoading}
        qaId="patients-pagination"
      />
    </Box>
  )
}

export default PatientTable
